import React, { useEffect, useMemo, useState } from 'react';
import { useOptionalWebSocket } from '../../../contexts/WebSocketContext';
import BattleRoomDialog from './BattleRoomDialog';
import './BattleRoomInterruptions.css';

const parsePhysicalRolls = (value) => {
  const text = String(value || '').trim();
  if (!text) return [];
  const faces = text.split(/[\s,]+/).map(Number);
  return faces.every((face) => Number.isInteger(face) && face >= 1 && face <= 1000) ? faces : null;
};

const BattleRoomInterruptions = ({ state, tokens = [], automaticRolls = true }) => {
  const { sendBattleRoomMutation } = useOptionalWebSocket() || {};
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [manualRoll, setManualRoll] = useState('');
  const [reactionActionId, setReactionActionId] = useState('');
  const [reactionPhysicalRolls, setReactionPhysicalRolls] = useState('');
  const [now, setNow] = useState(Date.now());
  const concentration = useMemo(() => Object.values(state?.pendingConcentrationDecisions || {}).find((entry) => entry?.status === 'pending') || null, [state]);
  const reaction = useMemo(() => Object.values(state?.reactionWindows || {}).find((entry) => entry?.status === 'pending') || null, [state]);
  const interruption = concentration || reaction;
  const reactionChoices = useMemo(() => (
    Array.isArray(reaction?.actionChoices) ? reaction.actionChoices : []
  ), [reaction]);
  const selectedReactionAction = reactionChoices.find((choice) => choice.actionId === reactionActionId)
    || (reactionChoices.length === 1 ? reactionChoices[0] : null);
  const parsedReactionPhysicalRolls = useMemo(() => parsePhysicalRolls(reactionPhysicalRolls), [reactionPhysicalRolls]);
  const tokenName = (id) => tokens.find((token) => [token.id, token.combatantId, token.characterId, token.monsterId].map(String).includes(String(id)))?.name || 'Creature';

  useEffect(() => {
    // SRD 5.1 opportunity attacks use one melee attack. A single legal attack
    // remains one-click; multiple attacks require an explicit GM/controller
    // choice rather than an engine guess. date_of_change: 2026-09-06
    setReactionActionId(reactionChoices.length === 1 ? reactionChoices[0].actionId : '');
    setReactionPhysicalRolls('');
  }, [reaction?.windowId, reactionChoices]);

  useEffect(() => {
    if (!interruption) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(timer);
  }, [interruption]);

  const decide = async (eventName, payload) => {
    if (typeof sendBattleRoomMutation !== 'function') {
      setError('Reconnect to the BattleRoom before resolving this decision.');
      return;
    }
    setPending(true);
    setError('');
    try {
      await sendBattleRoomMutation(eventName, payload);
    } catch (cause) {
      setError(cause?.message || 'The decision could not be saved.');
    } finally {
      setPending(false);
    }
  };

  if (!interruption) {
    const publicStatus = state?.interruptionStatus?.[0];
    return publicStatus ? <div className="br-interruption-waiting" role="status">Waiting for {publicStatus.type === 'concentration' ? 'a concentration decision' : 'an opportunity-attack decision'}…</div> : null;
  }

  if (concentration) {
    const replacement = concentration.kind === 'replacement';
    const expiry = new Date(concentration.expiresAt).getTime();
    const remainingSeconds = Number.isFinite(expiry) ? Math.max(0, Math.ceil((expiry - now) / 1000)) : 0;
    return (
      <BattleRoomDialog
        visible
        eyebrow="Rules interruption"
        title={replacement ? 'Replace concentration?' : 'Concentration check'}
        description={replacement
          ? `${tokenName(concentration.combatantId)} is already concentrating. The new effect remains staged until you choose.`
          : `${tokenName(concentration.combatantId)} took ${concentration.damage} damage and must make a DC ${concentration.dc} Constitution save.`}
        footer={replacement ? (
          <>
            <button type="button" onClick={() => decide('concentration:resolve', { decisionId: concentration.decisionId, replace: false })} disabled={pending}>Keep current effect</button>
            <button type="button" data-battle-dialog-autofocus onClick={() => decide('concentration:resolve', { decisionId: concentration.decisionId, replace: true })} disabled={pending}>Replace with new effect</button>
          </>
        ) : (
          <>
            <button type="button" onClick={() => decide('concentration:resolve', { decisionId: concentration.decisionId, manualRoll: manualRoll === '' ? undefined : Number(manualRoll) })} disabled={pending || (manualRoll !== '' && (Number(manualRoll) < 1 || Number(manualRoll) > 20))}>{manualRoll === '' ? 'Roll on server' : 'Use manual roll'}</button>
          </>
        )}
      >
        {replacement ? (
          <dl className="br-interruption-details"><div><dt>Current</dt><dd>{concentration.current?.sourceName}</dd></div><div><dt>Proposed</dt><dd>{concentration.proposed?.sourceName}</dd></div></dl>
        ) : (
          <label className="br-interruption-manual">Optional physical d20 result<input type="number" min="1" max="20" value={manualRoll} onChange={(event) => setManualRoll(event.target.value)} placeholder="Leave blank for server roll" /><span>The server adds the structured modifier ({Number(concentration.modifier) >= 0 ? '+' : ''}{concentration.modifier}).</span></label>
        )}
        <p>{remainingSeconds} seconds remaining. If time expires, {replacement ? 'the current effect stays active' : 'the server rolls the save'}.</p>
        {pending && <p role="status">Resolving concentration…</p>}
        {error && <p className="br-interruption-error" role="alert">{error}</p>}
      </BattleRoomDialog>
    );
  }

  const remainingSeconds = Math.max(0, Math.ceil((new Date(reaction.expiresAt).getTime() - now) / 1000));
  const attackSummary = selectedReactionAction || reaction.attackSummary;
  const requiresAttackChoice = reactionChoices.length > 1 && !selectedReactionAction;
  // Physical rolls are raw faces in exact engine-consumption order. The server
  // remains authoritative for each die size and rejects the entire mutation if
  // the sequence is incomplete or contains unused faces. date_of_change: 2026-09-06
  const requiresPhysicalRolls = automaticRolls === false;
  const physicalRollsInvalid = requiresPhysicalRolls
    && (!parsedReactionPhysicalRolls || parsedReactionPhysicalRolls.length === 0);
  const submitDescription = [
    requiresAttackChoice ? 'reaction-attack-choice-hint' : null,
    requiresPhysicalRolls ? 'reaction-physical-rolls-hint' : null
  ].filter(Boolean).join(' ') || undefined;
  return (
    <BattleRoomDialog
      visible
      eyebrow="Reaction window"
      title="Opportunity attack?"
      description={`${tokenName(reaction.moverId)} is paused before leaving ${tokenName(reaction.reactorId)}’s reach.`}
      footer={(
        <>
          <button type="button" onClick={() => decide('reaction:resolve', { windowId: reaction.windowId, decision: 'decline' })} disabled={pending}>Decline</button>
          <button
            type="button"
            data-battle-dialog-autofocus={reactionChoices.length <= 1 && !requiresPhysicalRolls ? true : undefined}
            onClick={() => decide('reaction:resolve', {
              windowId: reaction.windowId,
              decision: 'react',
              ...(selectedReactionAction?.actionId ? { actionId: selectedReactionAction.actionId } : {}),
              ...(requiresPhysicalRolls ? { manualRolls: parsedReactionPhysicalRolls } : {})
            })}
            disabled={pending || requiresAttackChoice || physicalRollsInvalid}
            aria-describedby={submitDescription}
          >
            Make opportunity attack
          </button>
        </>
      )}
    >
      {reactionChoices.length > 1 && (
        <label className="battle-room-dialog__field" htmlFor="reaction-attack-choice">
          <span className="battle-room-dialog__label">Melee attack</span>
          <select
            id="reaction-attack-choice"
            aria-label="Melee attack"
            className="battle-room-dialog__select"
            data-battle-dialog-autofocus
            value={reactionActionId}
            onChange={(event) => setReactionActionId(event.target.value)}
          >
            <option value="">Choose an attack</option>
            {reactionChoices.map((choice) => (
              <option key={choice.actionId} value={choice.actionId}>
                {choice.name} ({choice.attackBonus >= 0 ? '+' : ''}{choice.attackBonus}; {choice.damage} {choice.damageType})
              </option>
            ))}
          </select>
          <span id="reaction-attack-choice-hint" className="battle-room-dialog__hint">
            Opportunity Attack uses one embedded melee attack, never Multiattack.
          </span>
        </label>
      )}
      {requiresPhysicalRolls && (
        <label className="battle-room-dialog__field br-reaction-physical-rolls" htmlFor="reaction-physical-rolls">
          <span className="battle-room-dialog__label">Physical die faces, in order</span>
          <input
            id="reaction-physical-rolls"
            aria-label="Physical die faces, in order"
            className="battle-room-dialog__input"
            type="text"
            inputMode="numeric"
            pattern="[0-9, ]+"
            placeholder="Example: 17, 6, 2"
            data-battle-dialog-autofocus={reactionChoices.length <= 1 ? true : undefined}
            value={reactionPhysicalRolls}
            onChange={(event) => {
              setReactionPhysicalRolls(event.target.value);
              setError('');
            }}
            aria-invalid={reactionPhysicalRolls !== '' && parsedReactionPhysicalRolls === null ? 'true' : undefined}
            aria-describedby="reaction-physical-rolls-hint"
          />
          <span id="reaction-physical-rolls-hint" className="battle-room-dialog__hint">
            Enter raw faces separated by commas: attack d20 face(s) first; on a hit, each {attackSummary?.damage || 'damage'} die; then any triggered save d20s. Double damage dice on a critical. The server validates the exact sequence and commits nothing on error.
          </span>
        </label>
      )}
      <dl className="br-interruption-details"><div><dt>Reactor</dt><dd>{tokenName(reaction.reactorId)}</dd></div><div><dt>Mover</dt><dd>{tokenName(reaction.moverId)}</dd></div><div><dt>Attack</dt><dd>{attackSummary?.name || 'Choose a melee attack'} {attackSummary && <>{Number(attackSummary.attackBonus) >= 0 ? '+' : ''}{attackSummary.attackBonus ?? '?'} · {attackSummary.damage || 'damage not configured'} {attackSummary.damageType || ''}</>}</dd></div><div><dt>Time remaining</dt><dd>{remainingSeconds} seconds</dd></div></dl>
      <p>If time expires, the reaction is declined without spending it and movement continues.</p>
      {pending && <p role="status">Resolving reaction…</p>}
      {error && <p className="br-interruption-error" role="alert">{error}</p>}
    </BattleRoomDialog>
  );
};

export default BattleRoomInterruptions;
